import { OrgConsentEvent } from '@experiences/telemetry';
import {
    useNavigateWithParams,
    useShowDialog,
} from '@experiences/util';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import useSWR from 'swr';

import { notificationType } from '../../common/constants/Constant';
import { routePaths } from '../../common/constants/routePaths';
import { useUiSnackBar } from '../../common/hooks/useUiSnackBar';
import type { Consent } from '../../services/identity/ConsentService';
import {
    deleteConsent,
    getAllConsents,
    updateConsent,
} from '../../services/identity/ConsentService';
import { accountGlobalId } from '../../store/selectors';
import { useTelemetryHelper } from '../../telemetry/TelemetryHelper';
import { MAX_CONSENTS } from './ConsentConstants';

const initialState = {
    consents: [],
    isLoading: false,
    enableCreate: false,
    consentIdSearchParam: null,
    mutate: () => {},
    getAvailableConsentPriorities: () => [],
    onEdit: () => {},
    onDelete: () => {},
    onActivateDeactivate: () => {},
    onCreate: () => {},
    onViewUserRecords: () => {},
    isCardView: true,
    toggleCardView: () => {},
};

const ConsentListContext = createContext<{
    consents: Consent[] | undefined;
    isLoading: boolean;
    enableCreate: boolean;
    consentIdSearchParam: string | null;
    mutate: () => void;
    getAvailableConsentPriorities: (consentId?: string) => number[];
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
    onActivateDeactivate: (activate: boolean, consent: Consent) => void;
    onCreate: () => void;
    onViewUserRecords: (id: string) => void;
    isCardView: boolean;
    toggleCardView: () => void;
}>(initialState);

export const useConsentList = () => useContext(ConsentListContext);

export const ConsentListProvider = ({ children }: { children: React.ReactNode }) => {
    const { formatMessage: translate } = useIntl();

    const navigate = useNavigateWithParams();
    const [ searchParams, setSearchParams ] = useSearchParams();
    const consentIdSearchParam = searchParams.get('consentId');

    const { logEvent } = useTelemetryHelper();

    const createDialog = useShowDialog();
    const { createNotification } = useUiSnackBar();
    const partitionGlobalId = useSelector(accountGlobalId);

    const [ isCardView, setIsCardView ] = useState(true);
    const [ isCardViewToggled, setIsCardViewToggled ] = useState(false);

    const {
        data: rawConsents, isLoading, mutate,
    } = useSWR({ partitionGlobalId }, getAllConsents);

    // Flatten the data structure to make title searchable
    const consents = useMemo(() => rawConsents?.map(consent => ({
        ...consent,
        title: consent.content?.title || '', // Add flattened title for search
    })), [ rawConsents ]);

    // Set the view to card view if there are more than 4 consents
    useEffect(() => {
        if (consents && consents.length > 4 && !isCardViewToggled) {
            setIsCardView(false);
        }
    }, [ consents, setIsCardView, isCardViewToggled ]);

    const toggleCardView = useCallback(() => {
        setIsCardView(!isCardView);
        setIsCardViewToggled(true);
    }, [ setIsCardView, isCardView ]);

    const getAvailableConsentPriorities = useCallback(
        (consentId?: string) => Array.from({ length: MAX_CONSENTS }, (_, priority) => priority + 1)
            .filter(priority => !consents?.some(consent => consent.priority === priority && consent.id !== consentId)),
        [ consents ],
    );

    const onEdit = useCallback((id: string) => {
        logEvent(OrgConsentEvent.EditConsent, { consentId: id });
        navigate(routePaths.adminUserConsentEdit.replace(':id', id));
    }, [ logEvent, navigate ]);

    const onDelete = useCallback(async (id: string) => {
        const proceed = await createDialog({
            title: translate({ id: 'CLIENT_USER_DELETE_CONSENT_TITLE' }),
            body: translate({ id: 'CLIENT_USER_DELETE_CONSENT_SUBTITLE' }),
            icon: 'error',
            showCancel: true,
            primaryButtonText: translate({ id: 'CLIENT_DELETE' }),
        });

        if (proceed) {
            await deleteConsent({
                partitionGlobalId,
                consentId: id,
            });
            mutate();
            consentIdSearchParam && setSearchParams(undefined);
            createNotification(
                translate({ id: 'CLIENT_ORG_CONSENT_DELETE_SUCCESS' }),
                notificationType.SUCCESS
            );
            logEvent(OrgConsentEvent.DeleteConsent, { consentId: id });
        }
    }, [ createDialog, translate, logEvent, partitionGlobalId, createNotification, mutate, setSearchParams, consentIdSearchParam ]);

    const onActivateDeactivate = useCallback(async (activate: boolean, consent: Consent) => {
        let proceed = false;
        if (activate) {
            proceed = await createDialog({
                title: translate({ id: 'CLIENT_USER_TOGGLE_CONSENT_ACTIVE_TITLE' }),
                body: (
                    <>
                        <FormattedMessage id="CLIENT_USER_TOGGLE_CONSENT_ACTIVE_SUBTITLE" />
                        <ul>
                            <FormattedMessage
                                id="CLIENT_USER_TOGGLE_CONSENT_ACTIVE_BULLETS"
                                values={{
                                    li: (chunk: React.ReactNode[]) => <li>
                                        {chunk}
                                    </li>,
                                }}
                            />
                        </ul>
                    </>
                ),
                icon: 'warning',
                showCancel: true,
                unclosable: true,
                primaryButtonText: translate({ id: 'CLIENT_ACTIVATE_BUTTON' }),
            });
        } else {
            proceed = await createDialog({
                title: translate({ id: 'CLIENT_USER_TOGGLE_CONSENT_INACTIVE_TITLE' }),
                body: translate({ id: 'CLIENT_USER_TOGGLE_CONSENT_INACTIVE_SUBTITLE' }),
                icon: 'warning',
                showCancel: true,
                unclosable: true,
                primaryButtonText: translate({ id: 'CLIENT_DEACTIVATE_ACTION' }),
            });
        }

        if (proceed) {
            await updateConsent({
                partitionGlobalId,
                consentId: consent.id,
                body: {
                    ...consent,
                    content: undefined, // Set to undefined since we don't have a content object for multiple orgs
                    userReconsentNeeded: false,
                    isActive: activate,
                },
            });
            mutate();
            createNotification(
                translate({ id: 'CLIENT_ORG_CONSENT_SAVE_SUCCESS' }),
                notificationType.SUCCESS
            );
            logEvent(activate ? OrgConsentEvent.ActivateConsent : OrgConsentEvent.DeactivateConsent, { consentId: consent.id });
        }
    }, [ createDialog, translate, logEvent, partitionGlobalId, createNotification, mutate ]);

    const onCreate = useCallback(() => {
        logEvent(OrgConsentEvent.CreateConsent);
        navigate(routePaths.adminUserConsentCreate);
    }, [ logEvent, navigate ]);

    const onViewUserRecords = useCallback((id: string) => {
        logEvent(OrgConsentEvent.ViewUserRecords, { consentId: id });
        setSearchParams({ consentId: id });
    }, [ setSearchParams, logEvent ]);

    const value = useMemo(() => ({
        consents,
        isLoading,
        enableCreate: !!consents && consents.length < MAX_CONSENTS,
        consentIdSearchParam,
        mutate,
        getAvailableConsentPriorities,
        onEdit,
        onDelete,
        onActivateDeactivate,
        onCreate,
        onViewUserRecords,
        isCardView,
        toggleCardView,
    }), [
        consents,
        isLoading,
        consentIdSearchParam,
        mutate,
        getAvailableConsentPriorities,
        onEdit,
        onDelete,
        onActivateDeactivate,
        onCreate,
        onViewUserRecords,
        isCardView,
        toggleCardView,
    ]);

    return (
        <ConsentListContext.Provider value={value}>
            {children}
        </ConsentListContext.Provider>
    );
};
